import { useMemo, useState } from "react"
import { ChevronRight, ChevronUp, ChevronDown, Shuffle } from "lucide-react"
import { cn } from "@/lib/cn"
import { fromJson, hydrateAst, flatten, type FlatLeaf } from "@/lib/predicate-ast"
import type {
  AgentPoolOption,
  CustomFieldCatalog,
  EscalationPolicy,
  PolicyAgentOption,
  PolicyPriority,
  PolicyUserOption,
  TicketQueueOption,
} from "@/types/escalation_policy"

// Detached-card row for the routing-rules list. Each rule renders as its
// own floating card with a 2px brand-red ring on the selected one. No
// inline editor — clicking the card body fires `onSelect(policy.id)` and
// the right pane (PolicyEditorPanel) takes over.
//
// Card layout:
//   ┌──────────────────────────────────────────────────────┐
//   │ ⋮⋮  [N]  Rule Name              ◯◉  Active           │
//   │          summary description                  ›       │
//   │          [chip] [chip] [chip]                         │
//   └──────────────────────────────────────────────────────┘

type Props = {
  policy: EscalationPolicy
  users: PolicyUserOption[]
  agents: PolicyAgentOption[]
  agentPools: AgentPoolOption[]
  ticketQueues: TicketQueueOption[]
  customFieldCatalog: CustomFieldCatalog
  /** True in draft view — gates drag-handle. */
  editable: boolean
  /** True if this row is the one currently being edited in the right pane. */
  selected: boolean
  /** Index of this row in the FULL policies array (not the filtered view).
   *  Used as the drop-target index for native HTML5 DnD reorder. */
  fullIndex: number
  /** Sum of `timesMatched` across every rule in the active set. Used to
   *  compute this row's share-of-matches in the upper-right counter. */
  setTotalMatches: number
  onSelect: (id: number) => void
  /** Called with (draggedId, dropIndex) when the operator drops a row.
   *  No-op when editable is false. */
  onReorder?: (fromId: number, toIndex: number) => void
  /** Total rows in the FULL policies array — used to disable the
   *  mobile down-button on the last row. */
  fullCount: number
}

export function PolicyListRow({
  policy,
  users,
  agents,
  agentPools,
  ticketQueues,
  customFieldCatalog,
  editable,
  selected,
  fullIndex,
  fullCount,
  setTotalMatches,
  onSelect,
  onReorder,
}: Props) {
  const [dragOver, setDragOver] = useState(false)
  const [dragging, setDragging] = useState(false)

  const removed = policy.diffStatus === "removed"

  const summaryLine = useMemo(() => buildSummaryLine(policy), [policy])
  const matchChips  = useMemo(() => buildMatchChips(policy, customFieldCatalog), [policy, customFieldCatalog])
  const routingChip = useMemo(() => buildRoutingChip(policy, users, agents, agentPools, ticketQueues), [policy, users, agents, agentPools, ticketQueues])
  const isCatchAll  = matchChips.length === 0 && summaryLine === null

  const handleCardClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement
    if (t.closest("[data-row-stop]")) return
    if (removed) return
    onSelect(policy.id)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          if (!removed) onSelect(policy.id)
        }
      }}
      draggable={editable && !!onReorder}
      onDragStart={(e) => {
        if (!editable || !onReorder) return
        setDragging(true)
        e.dataTransfer.effectAllowed = "move"
        e.dataTransfer.setData("text/x-policy-id", String(policy.id))
      }}
      onDragEnd={() => {
        setDragging(false)
        setDragOver(false)
      }}
      onDragOver={(e) => {
        if (!editable || !onReorder) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!editable || !onReorder) return
        e.preventDefault()
        setDragOver(false)
        const raw = e.dataTransfer.getData("text/x-policy-id")
        const fromId = parseInt(raw, 10)
        if (!Number.isFinite(fromId) || fromId === policy.id) return
        onReorder(fromId, fullIndex)
      }}
      className={cn(
        // Each rule is its own floating card. No visible outline by
        // default — the slight panel-color difference against the page
        // background separates the cards. Selection is the only visible
        // ring on the card. Diff status (NEW / EDITED / MOVED) is
        // intentionally NOT surfaced here; the operator already knows
        // they're editing a draft from the page-level strip.
        "group relative cursor-pointer rounded-card bg-app-panel2 transition",
        selected
          ? "ring-2 ring-brand-red bg-brand-red/[0.06]"
          : "hover:bg-white/[0.03]",
        removed && "opacity-60 cursor-not-allowed",
        !policy.active && !removed && !selected && "opacity-50",
        dragging && "opacity-40",
        dragOver && "ring-2 ring-brand-red/60",
      )}
    >
      <div className="grid grid-cols-[28px_28px_minmax(0,1fr)_16px] items-start gap-2.5 px-3 py-2.5 lg:grid-cols-[20px_28px_minmax(0,1fr)_16px]">
        {/* Drag handle (desktop) / move buttons (mobile + tablet).
            Drag is mouse-only; touch users get explicit ↑↓ buttons. */}
        <div
          data-row-stop
          className={cn(
            "select-none pt-1.5 text-app-subtle",
            editable ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed opacity-30",
            "hidden lg:block",
          )}
          aria-hidden
          title={editable ? "Drag to reorder" : "Reorder available in draft view"}
        >
          ⋮⋮
        </div>
        <div data-row-stop className="flex flex-col gap-0.5 lg:hidden">
          <button
            type="button"
            onClick={() => onReorder && fullIndex > 0 && onReorder(policy.id, fullIndex - 1)}
            disabled={!editable || !onReorder || fullIndex === 0}
            aria-label="Move up"
            className="grid h-6 w-6 place-items-center rounded-control border border-border bg-white/[0.03] text-app-muted transition hover:bg-white/10 hover:text-app-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            onClick={() => onReorder && fullIndex < fullCount - 1 && onReorder(policy.id, fullIndex + 1)}
            disabled={!editable || !onReorder || fullIndex >= fullCount - 1}
            aria-label="Move down"
            className="grid h-6 w-6 place-items-center rounded-control border border-border bg-white/[0.03] text-app-muted transition hover:bg-white/10 hover:text-app-text disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronDown size={14} />
          </button>
        </div>

        {/* Position — borderless pill that just sits on the card */}
        <div className="pt-0.5">
          <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-control bg-white/[0.04] px-1 text-[12px] font-semibold tabular-nums text-app-muted">
            {policy.position + 1}
          </span>
        </div>

        {/* Name + summary + chips */}
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "truncate text-[15px] font-semibold leading-tight text-app-text",
                removed && "line-through",
              )}
            >
              {policy.name}
            </span>
          </div>

          {isCatchAll ? (
            <div className="text-[11px] italic text-status-success">
              catch-all (matches every escalation)
            </div>
          ) : summaryLine ? (
            <div className="truncate text-[12px] text-app-muted">{summaryLine}</div>
          ) : null}

          {(matchChips.length > 0 || routingChip) && (
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              {matchChips.map((c, i) => (
                <Chip key={`m-${i}`} chip={c} />
              ))}
              {routingChip && <Chip chip={routingChip} />}
            </div>
          )}
        </div>

        {/* Chevron — vertically centered with the card body, mirrors the
            mockup. Sits in its own grid column so it doesn't shift when
            the body reflows. */}
        <ChevronRight
          size={16}
          className="self-center text-app-subtle transition group-hover:text-app-muted group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>

      {/* Lifetime match counter — upper-right corner, right-aligned.
          Always rendered, even at 0/0%, so the column stays
          consistent across rows and "this rule has never matched" is
          a visible signal rather than an absent one. */}
      <div className="pointer-events-none absolute right-8 top-2 text-right text-[11px] tabular-nums text-app-subtle">
        <span className="font-medium text-app-muted">
          {policy.timesMatched.toLocaleString()}
        </span>
        <span className="ml-1">
          ({setTotalMatches > 0
            ? Math.round((policy.timesMatched / setTotalMatches) * 100)
            : 0}%)
        </span>
      </div>
    </div>
  )
}

// -- chips ---------------------------------------------------------------

type ChipKind = "system" | "appuser" | "convo" | "reason" | "topic" | "repeat" | "routing"

type ChipSpec = {
  kind: ChipKind
  label: string
  prefix?: string   // small leading tag, e.g. "sys"
  icon?: "shuffle"  // optional leading icon, currently only routing uses it
}

function Chip({ chip }: { chip: ChipSpec }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-chip border px-2 py-0.5 text-[11px]",
        chip.kind === "system"  ? "border-vip-gold/40 bg-vip-goldMute/40 text-app-text"
        : chip.kind === "routing" ? "border-border bg-white/5 text-app-text"
                                  : "border-border bg-white/[0.03] text-app-text",
      )}
    >
      {chip.icon === "shuffle" && <Shuffle size={11} className="text-app-subtle" />}
      {chip.prefix && (
        <span
          className={cn(
            "rounded-control px-1 text-[9px] font-semibold uppercase tracking-wide",
            chip.kind === "system"
              ? "bg-vip-gold/30 text-vip-goldSoft"
              : "bg-white/10 text-app-subtle",
          )}
        >
          {chip.prefix}
        </span>
      )}
      <span className="font-medium">{chip.label}</span>
    </span>
  )
}

// -- helpers (re-exported so other surfaces can reuse) --------------------

export function priorityTone(p: PolicyPriority): "error" | "warning" | "neutral" | "success" {
  switch (p) {
    case "urgent": return "error"
    case "high":   return "warning"
    case "normal": return "neutral"
    case "low":    return "success"
  }
}

// Compact one-line summary for the description line below the name.
// Returns null when there's nothing to show (the caller renders a
// catch-all italic instead).
function buildSummaryLine(p: EscalationPolicy): string | null {
  const parts: string[] = []
  if (p.match_min_recent_escalations) {
    const days = p.match_recent_window_days ?? 7
    parts.push(`≥ ${p.match_min_recent_escalations} escalations in last ${days}d`)
  }
  if (p.match_reason_keys_include.length) {
    parts.push(`reason: ${p.match_reason_keys_include.slice(0, 2).join(", ")}${p.match_reason_keys_include.length > 2 ? "…" : ""}`)
  }
  if (p.match_topic_keys_include.length) {
    parts.push(`topic: ${p.match_topic_keys_include.slice(0, 2).join(", ")}${p.match_topic_keys_include.length > 2 ? "…" : ""}`)
  }
  return parts.length ? parts.join(" · ") : null
}

// Render the predicate tree + repeat-escalator dimension as a list of
// chip specs. System fields (reserved keys) get a gold "sys" prefix to
// match the mockup; everything else uses the plain border chip style.
function buildMatchChips(p: EscalationPolicy, catalog: CustomFieldCatalog): ChipSpec[] {
  const chips: ChipSpec[] = []

  if (p.match_custom_fields && Object.keys(p.match_custom_fields).length > 0) {
    const parsed = fromJson(p.match_custom_fields)
    if (parsed.ok) {
      const hydrated = hydrateAst(parsed.ast, catalog)
      const leaves = flatten(hydrated, catalog)
      const visible = leaves.slice(0, 4)
      for (const leaf of visible) {
        chips.push(predicateLeafToChip(leaf, catalog))
      }
      if (leaves.length > visible.length) {
        chips.push({ kind: "appuser", label: `+${leaves.length - visible.length} more` })
      }
    }
  }

  if (p.match_min_recent_escalations) {
    const days = p.match_recent_window_days ?? 7
    chips.push({
      kind:  "repeat",
      label: `${p.match_min_recent_escalations} escalations / ${days} day${days === 1 ? "" : "s"}`,
    })
  }

  return chips
}

function predicateLeafToChip(leaf: FlatLeaf, catalog: CustomFieldCatalog): ChipSpec {
  const opStr  = humanOp(leaf.op, leaf.fieldType, leaf.value)
  const valStr = humanValue(leaf)
  // Scope is "appuser" | "agent_convo"; reserved keys (system fields)
  // only live on appuser today.
  const isConvo  = leaf.scope === "agent_convo"
  const reserved = !isConvo &&
    catalog.appuser.some((d) => d.reserved && d.key === leaf.fieldKey)
  const fieldDisplay = isConvo ? `convo.${leaf.fieldKey}` : leaf.fieldKey
  const tail = valStr ? `${opStr} ${valStr}` : opStr
  return {
    kind:   reserved ? "system" : (isConvo ? "convo" : "appuser"),
    label:  `${fieldDisplay} ${tail}`.trim(),
    prefix: reserved ? "sys" : undefined,
  }
}

// Routing target as a single chip with a Shuffle icon to mirror the
// mockup. Returns null for "Leave unassigned" since there's nothing to
// surface.
function buildRoutingChip(
  p: EscalationPolicy,
  users: PolicyUserOption[],
  agents: PolicyAgentOption[],
  agentPools: AgentPoolOption[],
  ticketQueues: TicketQueueOption[],
): ChipSpec | null {
  switch (p.assignee_strategy) {
    case "unassigned":
      return null
    case "specific_user": {
      const u = users.find((x) => x.id === p.assignee_user_id)
      return { kind: "routing", icon: "shuffle", label: u ? u.name : "—" }
    }
    case "random_from_pool":
    case "round_robin_from_pool": {
      const pool = agentPools.find((x) => x.id === p.assignee_agent_pool_id)
      return { kind: "routing", icon: "shuffle", label: pool ? pool.name : "—" }
    }
    case "route_to_agent": {
      const a = agents.find((x) => x.id === p.reroute_agent_id)
      return { kind: "routing", icon: "shuffle", label: a ? a.name : "—" }
    }
    case "send_to_queue": {
      const q = ticketQueues.find((x) => x.id === p.assignee_ticket_queue_id)
      return { kind: "routing", icon: "shuffle", label: q ? q.name : "—" }
    }
  }
}

function humanOp(op: FlatLeaf["op"], fieldType: FlatLeaf["fieldType"], value: unknown): string {
  if (op === "present") return "is set"
  if (op === "blank")   return "is not set"
  if (op === "between") return "between"
  if (op === "matches") return "matches"
  if (op === "contains") return "contains"
  if (op === "neq")     return "≠"
  if (op === "gte")     return "≥"
  if (op === "lte")     return "≤"
  if (op === "gt")      return ">"
  if (op === "lt")      return "<"
  if (fieldType === "boolean") return value === true ? "is" : "is"
  return "="
}

function humanValue(leaf: FlatLeaf): string {
  if (leaf.op === "present" || leaf.op === "blank") return ""
  const v = leaf.value
  if (leaf.op === "between" && Array.isArray(v) && v.length === 2) {
    return `${formatScalar(v[0])}–${formatScalar(v[1])}`
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]"
    return `[${v.map(formatScalar).join(", ")}]`
  }
  return formatScalar(v)
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === "") return ""
  if (typeof v === "string") return v
  if (typeof v === "boolean") return v ? "true" : "false"
  return String(v)
}

// Kept for backwards compatibility — Index.tsx still imports this name
// although no current consumer uses the grid template. Removing the
// export is a follow-up.
export const POLICY_GRID_COLS =
  "grid grid-cols-[20px_28px_minmax(0,1fr)_16px] items-start gap-2.5"

export function buildIfSummary(p: EscalationPolicy): string[] {
  const lines: string[] = []
  if (p.match_reason_keys_include.length)  lines.push(`Reasons: ${p.match_reason_keys_include.join(", ")}`)
  if (p.match_reason_keys_exclude.length)  lines.push(`Exclude reasons: ${p.match_reason_keys_exclude.join(", ")}`)
  if (p.match_topic_keys_include.length)   lines.push(`Topics: ${p.match_topic_keys_include.join(", ")}`)
  if (p.match_topic_keys_exclude.length)   lines.push(`Exclude topics: ${p.match_topic_keys_exclude.join(", ")}`)
  if (p.match_min_recent_escalations) {
    const days = p.match_recent_window_days ?? 7
    lines.push(`≥ ${p.match_min_recent_escalations} escalations in last ${days}d`)
  }
  return lines
}

export function buildThenSummary(
  p: EscalationPolicy,
  users: PolicyUserOption[],
  agents: PolicyAgentOption[],
  agentPools: AgentPoolOption[],
  ticketQueues: TicketQueueOption[],
): string[] {
  const lines: string[] = []
  switch (p.assignee_strategy) {
    case "unassigned":
      lines.push("Leave unassigned")
      break
    case "specific_user": {
      const u = users.find((x) => x.id === p.assignee_user_id)
      lines.push(`Assign to ${u ? u.name : "—"}`)
      break
    }
    case "random_from_pool":
    case "round_robin_from_pool": {
      const pool = agentPools.find((x) => x.id === p.assignee_agent_pool_id)
      const label = p.assignee_strategy === "random_from_pool" ? "Random pool" : "Round robin"
      lines.push(`${label}: ${pool ? pool.name : "—"}`)
      if (p.prefer_topic_affinity) lines.push("Prefer topic affinity")
      break
    }
    case "route_to_agent": {
      const a = agents.find((x) => x.id === p.reroute_agent_id)
      lines.push(`Reroute to AI agent: ${a ? a.name : "—"}`)
      break
    }
    case "send_to_queue": {
      const q = ticketQueues.find((x) => x.id === p.assignee_ticket_queue_id)
      lines.push(`Send to queue: ${q ? q.name : "—"}`)
      break
    }
  }
  if (p.priority_override) lines.push(`Priority → ${p.priority_override}`)
  const slaParts: string[] = []
  if (p.sla_first_response_minutes != null) slaParts.push(`first ${p.sla_first_response_minutes}m`)
  if (p.sla_idle_response_minutes  != null) slaParts.push(`idle ${p.sla_idle_response_minutes}m`)
  if (p.sla_resolution_minutes     != null) slaParts.push(`resolve ${p.sla_resolution_minutes}m`)
  if (slaParts.length) lines.push(`SLA: ${slaParts.join(", ")}`)
  return lines
}
